import {
  Directive,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ContentChildren,
  QueryList,
  AfterContentInit,
  NgZone,
} from '@angular/core';
import { zip, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AmapMarkerClustererService, AMapMarkerClusterer } from './amap-marker-clusterer.service';
import { LoggerService } from '../../shared/logger/logger.service';
import { EventBinderService } from '../../shared/event-binder.service';
import { getOptions, ChangeFilter } from '../../utils';
import { PixelService } from '../../shared/pixel.service';
import { SizeService } from '../../shared/size.service';
import { AmapMarkerDirective } from '../amap-marker/amap-marker.directive';
import { AMapService } from '../../shared/amap.service';

const TAG = 'amap-marker-clusterer';
const MarkerClusterOptions = [
  'gridSize',
  'minClusterSize',
  'maxZoom',
  'averageCenter',
  'styles',
  'renderClusterMarker',
  'zoomOnClick',
];

@Directive({
  selector: 'amap-marker-clusterer',
  exportAs: 'marker-clusterer',
  providers: [AmapMarkerClustererService],
})
export class AmapMarkerClustererDirective
  implements OnInit, OnChanges, OnDestroy, AfterContentInit {
  // ---- Options ----
  /**
   * èåè®¡ç®æ¶ç½æ ¼çåç´ å¤§å°ï¼é»è®¤60
   */
  @Input() gridSize: number;
  /**
   * èåçæå°æ°éãé»è®¤å¼ä¸º2ï¼å³å°äº2ä¸ªç¹åä¸è½æä¸ºä¸ä¸ªèå
   */
  @Input() minClusterSize: number;
  /**
   * æå¤§çèåçº§å«ï¼å¤§äºè¯¥çº§å«å°±ä¸è¿è¡ç¸åºçèåãé»è®¤å¼ä¸º18
   */
  @Input() maxZoom: number;
  /**
   * èåç¹çå¾æ ä½ç½®æ¯å¦æ¯ææèååç¹çä¸­å¿ç¹ãé»è®¤ä¸ºå¦
   */
  @Input() averageCenter: boolean;
  /**
   * æå®èååçç¹æ è®°çå¾æ æ ·å¼ï¼å¯ç¼ºçï¼ç¼ºçæ¶ä¸ºé»è®¤æ ·å¼
   */
  @Input() styles: AMapMarkerClusterer.StyleObject[];
  /**
   * è¯¥æ¹æ³ç¨æ¥å®ç°èåç¹çèªå®ä¹ç»å¶
   */
  @Input() renderClusterMarker: (obj: {
    count: number;
    markers: AMap.Marker<any>[];
    marker: AMap.Marker<any>;
  }) => void;
  /**
   * ç¹å»èåç¹æ¶ï¼æ¯å¦æ£å¼ï¼é»è®¤å¼ä¸ºï¼true
   */
  @Input() zoomOnClick: boolean;

  // ---- Events ----
  @Output() naReady = new EventEmitter();
  @Output() naClick: EventEmitter<any>;

  // amap-marker directives:
  @ContentChildren(AmapMarkerDirective) markerList = new QueryList<AmapMarkerDirective>();

  private inited = false;

  constructor(
    protected os: AmapMarkerClustererService,
    protected binder: EventBinderService,
    private amaps: AMapService,
    private pixels: PixelService,
    private sizes: SizeService,
    private logger: LoggerService,
    private ngZone: NgZone,
  ) {
    const target = this.os.get();
    this.naClick = this.binder.bindEvent(target, 'click');
  }

  ngOnDestroy() {
    this.os.destroy();
  }

  ngOnInit() {
    this.amaps.get().subscribe(() => {
      this.logger.d(TAG, 'initializing ...');
      const options = getOptions<AMapMarkerClusterer.Options>(this, MarkerClusterOptions);
      if (options.styles) {
        options.styles = options.styles.map(style => {
          if (style.size) {
            style.size = this.sizes.create(style.size) as AMap.Size;
          }
          if (style.offset) {
            style.offset = this.pixels.create(style.offset);
          }
          if (style.imageOffset) {
            style.imageOffset = this.pixels.create(style.imageOffset);
          }
          return style;
        });
      }
      this.logger.d(TAG, 'options:', options);
      this.os.create(options).subscribe();
      this.inited = true;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.inited) {
      return;
    }
    const filter = ChangeFilter.of(changes);
    const cluster = this.get();
    zip(filter.notEmpty<number>('gridSize'), cluster).subscribe(([v, c]) => c.setGridSize(v));
    zip(filter.notEmpty<number>('minClusterSize'), cluster).subscribe(([v, c]) =>
      c.setMinClusterSize(v),
    );
    zip(filter.has<number>('maxZoom'), cluster).subscribe(([v, c]) => c.setMaxZoom(v));
    zip(filter.has<boolean>('averageCenter'), cluster).subscribe(([v, c]) => c.setAverageCenter(v));
    zip(filter.has<AMapMarkerClusterer.StyleObject[]>('styles'), cluster).subscribe(([v, c]) =>
      c.setStyles(v),
    );
  }

  ngAfterContentInit() {
    this.updateMarkers().subscribe(c => {
      this.ngZone.run(() => this.naReady.emit(c));
      this.logger.d(TAG, 'markerClusterer is ready.');
    });
    this.markerList.changes.subscribe(() => this.updateMarkers().subscribe());
  }

  private updateMarkers() {
    return zip(combineLatest(this.markerList.map(d => d.get())), this.get()).pipe(
      map(([markers, cluster]) => {
        cluster.setMarkers(markers);
        return cluster;
      }),
    );
  }

  /**
   * è·åå·²åå»ºç AMap.MarkerClusterer å¯¹è±¡
   */
  get() {
    return this.os.get();
  }
}
